import { useMemo, useState } from 'react';
import type { AccountOption } from '../hooks/useAssetAccounts';
import { MAINTENANCE_GROUP_VALUES } from '../monitoringUtils';
import { UptimeChecklist, UptimeDrawer, UptimeFilterGroup, UptimeFilterPills } from '../../shared';
import { getMaintenanceGroupName } from './maintenanceGroupName';
import useFmText from './useFmText';

interface IMoreFiltersDrawerProps {
    showAccounts: boolean;
    accounts: AccountOption[];
    accountsLoading: boolean;
    selectedAccounts: string[];
    selectedMaintenanceGroups: string[];
    onApply: (accountKeys: string[], maintenanceGroups: string[]) => void;
    onClose: () => void;
}

const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter(item => item !== value) : [...list, value];

const MoreFiltersDrawer = ({
    showAccounts,
    accounts,
    accountsLoading,
    selectedAccounts,
    selectedMaintenanceGroups,
    onApply,
    onClose,
}: IMoreFiltersDrawerProps) => {
    const { t, formatNumber } = useFmText();
    const [selected, setSelected] = useState(selectedAccounts);
    const [maintenanceGroups, setMaintenanceGroups] = useState(selectedMaintenanceGroups);
    const [search, setSearch] = useState('');

    const visibleAccounts = useMemo(() => {
        const term = search.trim().toLowerCase();
        return term ? accounts.filter(account => account.name.toLowerCase().includes(term)) : accounts;
    }, [accounts, search]);

    return (
        <UptimeDrawer
            className='fleet-monitoring'
            title={t('filters.more')}
            onClose={onClose}
            footer={
                <>
                    <button
                        type='button'
                        className='fm-btn'
                        onClick={() => {
                            setSelected([]);
                            setMaintenanceGroups([]);
                            setSearch('');
                        }}
                    >
                        {t('moreFilters.clear')}
                    </button>
                    <button
                        type='button'
                        className='fm-btn fm-primary'
                        onClick={() => onApply(selected, maintenanceGroups)}
                    >
                        {t('moreFilters.apply')}
                    </button>
                </>
            }
        >
            {showAccounts && (
                <UptimeFilterGroup label={t('filters.account')}>
                    <UptimeChecklist
                        query={search}
                        onQueryChange={setSearch}
                        placeholder={t('moreFilters.searchAccount')}
                        items={visibleAccounts.map(account => ({
                            key: account.key,
                            label: account.name,
                            count: formatNumber(account.count),
                        }))}
                        selected={selected}
                        onToggle={key => setSelected(prev => toggle(prev, key))}
                        emptyText={accountsLoading ? t('moreFilters.loadingAccounts') : t('moreFilters.noAccount')}
                        hint={
                            accountsLoading && visibleAccounts.length
                                ? t('moreFilters.loadingAccounts')
                                : t('moreFilters.accountHint')
                        }
                        clearLabel={t('moreFilters.clearSelection', { count: formatNumber(selected.length) })}
                        onClear={() => setSelected([])}
                    />
                </UptimeFilterGroup>
            )}

            <UptimeFilterGroup label={t('filters.maintenanceGroup')}>
                <UptimeFilterPills
                    options={[...MAINTENANCE_GROUP_VALUES, '-'].map(value => ({
                        value,
                        label: getMaintenanceGroupName(t, value),
                    }))}
                    isSelected={value => maintenanceGroups.includes(value)}
                    onToggle={value => setMaintenanceGroups(prev => toggle(prev, value))}
                />
            </UptimeFilterGroup>
        </UptimeDrawer>
    );
};

export default MoreFiltersDrawer;
